import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import type { jhMenu_jhMenu_children } from 'src/queries/__generated__/jhMenu';
import { stripDomain } from 'src/util/sanitizeLink';
import { useBreakpointMatch } from 'src/hooks/useBreakpoint';

import { Svg } from '../Svg/Svg';
import { RichText } from '../RichText/RichText';

import { NavSubSub } from './NavSubSub';
import classes from './NavSub.scss';

type NavSubProps = {
    hideShopAll: boolean;
    parentTitle: string;
    parentUrl: string;
    setParentOverflow?: (value: boolean) => void;
    showSub: boolean;
    subNavs: (jhMenu_jhMenu_children | null)[];
    toggleList(value);
    nodeId: number;
};

export const NavSub = React.memo(function NavSub(props: NavSubProps) {
    const { isMob } = useBreakpointMatch();
    const { subNavs, parentTitle, showSub, toggleList, setParentOverflow } = props;
    const [openIndexes, toggleListSub] = useState(-1);
    const [overflow, setOverflow] = useState(true);

    return (
        <div
            data-test-id="sub-menu"
            data-accessibility="global"
            data-subnav-active={String(showSub)}
            data-nav-overflow={String(overflow)}
            className={classes.navSub}
            data-sub-top={props.nodeId}
            aria-label={`Sub menu for ${parentTitle}`}
            role="navigation"
        >
            <span className={classes.navSubHeader}>
                <button
                    className={classes.navSubHeaderBack}
                    onClick={() => {
                        toggleList(-1);
                        setParentOverflow?.(true);
                    }}
                    type="button"
                >
                    <Svg name="arrow-left" />
                    Back
                </button>
                <h2 className={classes.navSubHeaderTitle}>{parentTitle}</h2>
            </span>
            {isMob && (
                <ul className={classes.navSubList}>
                    {!props.hideShopAll && (
                        <li className={classes.navSubItem}>
                            <Link
                                to={props.parentUrl}
                                className={classnames({
                                    [classes.navSubLink]: true,
                                    [classes.navSubAll]: true,
                                })}
                            >
                                Shop All {parentTitle}
                            </Link>
                        </li>
                    )}
                    {subNavs.map((item, index) => {
                        if (!item) {
                            return null;
                        }

                        const isUrl = item.type === 'custom_url' || item.type === 'category';
                        const isCms = item.type === 'cms_block';
                        const hasChildren = item.children.length > 0;
                        const active = openIndexes === index;
                        const itemUrl = stripDomain(item.url);
                        const hideShopAll = Boolean(item.classes?.includes('hide-shop-all'));

                        return (
                            <li key={item.node_id} className={classes.navSubItem}>
                                {isUrl && (
                                    <>
                                        <Link
                                            to={item.url ?? ''}
                                            className={classes.navSubLink}
                                            onClick={(e) => {
                                                if (isMob && hasChildren) {
                                                    e.preventDefault();
                                                    const item = document.querySelector(
                                                        `[data-sub-top="${props.nodeId}"]`,
                                                    );

                                                    if (item) {
                                                        item.scrollTo(0, 0);
                                                    }

                                                    toggleListSub(index);
                                                    setOverflow?.(false);
                                                }
                                            }}
                                            aria-expanded={hasChildren ? active : undefined}
                                            aria-label={`Go to ${item.title}`}
                                        >
                                            {item.title}
                                            {hasChildren && <Svg name="arrow-right" aria-hidden="true" />}
                                        </Link>

                                        {hasChildren && (
                                            <NavSubSub
                                                subNavs={item.children}
                                                parentUrl={itemUrl || ''}
                                                parentTitle={item.title || 'unknown'}
                                                showSub={active}
                                                toggleList={(value) => toggleListSub(value)}
                                                setOverflow={setOverflow}
                                                hideShopAll={hideShopAll}
                                            />
                                        )}
                                    </>
                                )}
                                {isCms && showSub && item.html && (
                                    <div className={classes.navSubBlock}>
                                        <RichText content={item.html} />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            )}
            {!isMob &&
                subNavs.map((item) => {
                    if (!item) {
                        return null;
                    }

                    const isUrl = item.type === 'custom_url' || item.type === 'category';
                    const isCms = item.type === 'cms_block';

                    return (
                        <div key={item.node_id} className={classes.navSubContent}>
                            {isUrl && (
                                <Link to={item.url ?? ''} className={classes.navSubTitle}>
                                    {item.title}
                                </Link>
                            )}
                            {item.children.length > 0 && (
                                <ul className={classes.navSubList}>
                                    {item.children.map((subSubItem) => {
                                        if (!subSubItem) {
                                            return null;
                                        }

                                        return (
                                            <li key={subSubItem.node_id} className={classes.navSubItem}>
                                                <Link to={subSubItem.url ?? ''} className={classes.navSubLink}>
                                                    {subSubItem.title}
                                                </Link>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {isCms && showSub && item.html && (
                                <div className={classes.navSubBlock}>
                                    <RichText content={item.html} />
                                </div>
                            )}
                        </div>
                    );
                })}
        </div>
    );
});
